import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  BarChart3,
  Calculator,
  KeyRound,
  ShieldCheck,
  FileCode2,
  PieChart as PieChartIcon,
  Menu,
  X,
} from 'lucide-react';

const TOOL_COMPONENTS = {
  compound: lazy(() => import('../tools/CompoundInterestTool')),
  loan: lazy(() => import('../tools/LoanCalculatorTool')),
  password: lazy(() => import('../tools/PasswordGeneratorTool')),
  salary: lazy(() => import('../tools/SalaryPlannerTool')),
  hash: lazy(() => import('../tools/HashTool')),
  base64: lazy(() => import('../tools/Base64Tool')),
};

const TOOL_SECTIONS = [
  {
    id: 'finance',
    title: 'Финансы',
    items: [
      {
        id: 'compound',
        name: 'Сложный процент',
        description: 'Анализ роста капитала со сложными процентами и регулярными взносами.',
        icon: BarChart3,
      },
      {
        id: 'loan',
        name: 'Кредитный калькулятор',
        description: 'Графики платежей, переплата и сравнение типов погашения кредита.',
        icon: Calculator,
      },
      {
        id: 'salary',
        name: 'Планирование зарплаты',
        description: 'Распределение дохода по категориям с диаграммой и выгрузкой.',
        icon: PieChartIcon,
      },
    ],
  },
  {
    id: 'programming',
    title: 'Программирование (Computer Science)',
    items: [
      {
        id: 'password',
        name: 'Генератор паролей',
        description: 'Сильные случайные пароли в Web Worker с оценкой энтропии.',
        icon: KeyRound,
      },
      {
        id: 'hash',
        name: 'Hash',
        description: 'Популярные хэш-функции для текста и файлов.',
        icon: ShieldCheck,
      },
      {
        id: 'base64',
        name: 'Base64 Encode/Decode',
        description: 'Конвертация текста и файлов, поддержка выгрузки результатов.',
        icon: FileCode2,
      },
    ],
  },
];

const TOOL_ITEMS = TOOL_SECTIONS.flatMap((section) => section.items);

function ToolSkeleton() {
  return (
    <section className="tool-card tool-skeleton">
      <div className="skeleton-header">
        <div className="skeleton-title" />
        <div className="skeleton-pill" />
      </div>
      <div className="skeleton-body" />
    </section>
  );
}

export default function ToolsPage() {
  const { toolId } = useParams();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const { Component, meta } = useMemo(() => {
    if (toolId && TOOL_COMPONENTS[toolId]) {
      return { Component: TOOL_COMPONENTS[toolId], meta: TOOL_ITEMS.find((tool) => tool.id === toolId) };
    }
    return { Component: TOOL_COMPONENTS.compound, meta: TOOL_ITEMS[0] };
  }, [toolId]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  if (!toolId || !TOOL_COMPONENTS[toolId]) {
    return <Navigate to="/tools/compound" replace />;
  }

  return (
    <div className="tools-page">
      <aside className={`tool-sidebar ${menuOpen ? 'open' : ''}`} aria-label="Выбор инструмента">
        <div className="tool-sidebar__headline">
          <span className="tool-sidebar__title">Инструменты</span>
          <button
            type="button"
            className="icon-button sidebar-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Закрыть меню инструментов"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <nav className="tool-nav">
          {TOOL_SECTIONS.map((section) => (
            <div key={section.id} className="tool-nav__section">
              <span className="tool-nav__section-title">{section.title}</span>
              <div className="tool-nav__items">
                {section.items.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <NavLink
                      key={tool.id}
                      to={`/tools/${tool.id}`}
                      className={({ isActive }) => (isActive ? 'tool-link active' : 'tool-link')}
                    >
                      <Icon size={18} aria-hidden="true" />
                      <div className="tool-link__meta">
                        <span>{tool.name}</span>
                        <small>{tool.description}</small>
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
      {menuOpen ? <button type="button" className="tool-backdrop" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню" /> : null}
      <div className="tools-content">
        <header className="tools-header">
          <button
            type="button"
            className="icon-button mobile-menu"
            onClick={() => setMenuOpen(true)}
            aria-label="Открыть меню инструментов"
          >
            <Menu size={18} aria-hidden="true" />
          </button>
          <div className="tools-header__meta">
            <h1>{meta?.name}</h1>
            <p>{meta?.description}</p>
          </div>
        </header>
        <div className="tools-main">
          <Suspense fallback={<ToolSkeleton />}>
            <Component />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
